import api from './client';

// UI-facing shapes. The external email API uses different field names and
// wraps payloads (`{items:[...]}`, message detail under `message`); the mappers
// below adapt the raw responses to these stable shapes so the pages don't need
// to know the upstream naming. Field names confirmed against the live API
// 2026-07-13.
export interface EmailAddress {
  id?: string;
  email_address: string;
  source?: string | null;
  description?: string | null;
  status?: string | null;      // 'active' | 'paused'
  created_at?: string | number | null;
}

export interface EmailAttachment {
  id?: string;
  filename: string | null;
  mime_type: string | null;
  size?: number | null;
  content_base64?: string | null;   // absent upstream (stored by key) → no inline download
}

export interface EmailMessageSummary {
  id: string;
  from: string | null;
  to?: string | null;
  subject: string | null;
  created_at?: string | number | null;
  recipient_addresses?: string[] | null;   // used to filter the feed by mailbox
  attachments?: EmailAttachment[] | null;
}

export interface EmailMessage extends EmailMessageSummary {
  text_body?: string | null;
  html_body?: string | null;
}

export interface EmailConfigStatus {
  configured: boolean;
  health?: unknown;
}

export function getEmailConfigStatus(): Promise<EmailConfigStatus> {
  return api.get<EmailConfigStatus>('/admin/email/config-status');
}

export function getEmailDomains(): Promise<string[]> {
  return api.get<{ domains: string[] }>('/admin/email/domains').then((d) => d.domains);
}

export function listMailboxes(): Promise<EmailAddress[]> {
  return api.get<unknown>('/admin/email/addresses').then((d) => unwrapList(d).map(mapAddress));
}

export function createMailbox(input: { email_address: string; description?: string }): Promise<EmailAddress> {
  return api.post<unknown>('/admin/email/addresses', input).then((d) => mapAddress(unwrapObject(d, 'address')));
}

export function setMailboxPaused(id: string, paused: boolean): Promise<EmailAddress> {
  // Upstream PATCH expects `{ status }`, not `{ paused }`.
  return api
    .patch<unknown>(`/admin/email/addresses/${encodeURIComponent(id)}`, { status: paused ? 'paused' : 'active' })
    .then((d) => mapAddress(unwrapObject(d, 'address')));
}

export function listMessages(params?: { limit?: number }): Promise<EmailMessageSummary[]> {
  const suffix = params?.limit ? `?limit=${params.limit}` : '';
  return api.get<unknown>(`/admin/email/messages${suffix}`).then((d) => unwrapList(d).map(mapMessage));
}

export function getMessage(id: string): Promise<EmailMessage> {
  // Detail is wrapped: `{ message, routes, webhookDeliveries }`.
  return api
    .get<unknown>(`/admin/email/messages/${encodeURIComponent(id)}`)
    .then((d) => mapMessage(unwrapObject(d, 'message')));
}

// --- adapters -------------------------------------------------------------

function mapAddress(r: any): EmailAddress {
  return {
    id: r?.id,
    email_address: r?.email_address ?? '',
    source: r?.source ?? null,
    description: r?.description ?? null,
    status: r?.status ?? null,
    created_at: r?.created_at ?? null,
  };
}

function mapAttachment(r: any): EmailAttachment {
  return {
    id: r?.id,
    filename: r?.filename ?? null,
    mime_type: r?.mimeType ?? r?.mime_type ?? null,
    size: r?.size ?? null,
    content_base64: r?.content_base64 ?? null,
  };
}

function mapMessage(r: any): EmailMessage {
  return {
    id: r?.id,
    from: r?.from_email ?? r?.from ?? null,
    to: r?.to_header ?? r?.to ?? null,
    subject: r?.subject ?? null,
    created_at: r?.received_at ?? r?.created_at ?? null,
    recipient_addresses: Array.isArray(r?.recipient_addresses) ? r.recipient_addresses : null,
    attachments: Array.isArray(r?.attachments) ? r.attachments.map(mapAttachment) : [],
    text_body: r?.text_body ?? null,
    html_body: r?.html_body ?? null,
  };
}

// Upstream list responses are `{ items: [...] }`; tolerate a bare array or a
// legacy `addresses`/`messages` wrapper too.
function unwrapList(data: unknown): any[] {
  if (Array.isArray(data)) return data;
  const o = data as Record<string, unknown> | null;
  for (const k of ['items', 'addresses', 'messages']) {
    if (Array.isArray(o?.[k])) return o![k] as any[];
  }
  return [];
}

// Single-object responses may be bare or wrapped under a known key.
function unwrapObject(data: unknown, key: string): any {
  const o = data as Record<string, unknown> | null;
  if (o && typeof o === 'object' && o[key] && typeof o[key] === 'object') return o[key];
  return data;
}
